import React, { useState } from "react";
import {
  Dialog,
  DialogContent,
  Button,
  TextField,
  CircularProgress,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { createCcpDistrictUser } from "../../../../config/firebase";
import { getAuth, fetchSignInMethodsForEmail } from "firebase/auth";
import useData from "../hooks/useData";
import ModalDialogTitle from "../../../common/ModalDialogTitle";
import ModalActions from "../../../common/ModalActions";

const AddForm = ({ onSuccess }) => {
  const [name, setName] = useState("");
  const [district, setDistrict] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [ccpDistrictFlag, setCcpDistrictFlag] = useState(true);
  const [loading, setLoading] = useState(false);
  const [openAdd, setOpenAdd] = useState(false);
  const [error, setError] = useState("");

  const { data: districts, loading: districtsLoading } = useData();

  // Function to format phone number with dashes as the user types
  const formatPhoneNumber = (input) => {
    // Remove non-numeric characters
    const cleaned = ("" + input).replace(/\D/g, "");

    // Apply phone number formatting
    const match = cleaned.match(/^(\d{3})(\d{3})(\d{4})$/);
    if (match) {
      return `${match[1]}-${match[2]}-${match[3]}`;
    }
    return input;
  };

  const handlePhoneChange = (e) => {
    // Format phone number as the user types
    const formattedPhone = formatPhoneNumber(e.target.value);
    setPhone(formattedPhone);
  };

  const handleAddOpen = () => {
    setOpenAdd(true);
  };

  const handleAddClose = () => {
    setOpenAdd(false);
    setName("");
    setDistrict("");
    setEmail("");
    setPhone("");
    setPassword("");
    setError("");
  };

  const handleAddUser = async () => {
    setLoading(true); // Set loading to true when the user is being added
    const auth = getAuth();
    try {
      const signInMethods = await fetchSignInMethodsForEmail(auth, email);
      if (signInMethods.length > 0) {
        setError("Email already exists.");
        setLoading(false);
        return;
      }

      const { success, message } = await createCcpDistrictUser(
        name,
        district,
        email,
        password,
        phone,
        ccpDistrictFlag
      );
      if (success) {
        if (typeof onSuccess === "function") {
          onSuccess();
        }
        handleAddClose();
      } else {
        console.error(message);
        setError(message);
      }
    } catch (error) {
      console.error(error);
      setError("An error occurred while checking the email.");
    } finally {
      setLoading(false); // Set loading to false after the user is added
    }
  };
  // Extract unique district names
  const uniqueDistricts = districts
    .map((dist) => dist.district)
    .filter((value, index, self) => self.indexOf(value) === index);

  return (
    <>
      <Button
        variant="contained"
        color="primary"
        startIcon={<AddIcon />}
        onClick={handleAddOpen}
        style={{ marginBottom: 16, marginTop: 10 }}
      >
        Add New User
      </Button>
      <Dialog open={openAdd} onClose={handleAddClose}>
        <ModalDialogTitle onClose={handleAddClose}>
          Add New User
        </ModalDialogTitle>
        <DialogContent dividers>
          <>
            <TextField
              autoFocus
              margin="dense"
              label="Name"
              type="text"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
            {districtsLoading ? (
              <CircularProgress />
            ) : (
              <FormControl fullWidth margin="dense">
                <InputLabel id="district-label">District</InputLabel>
                <Select
                  labelId="district-label"
                  value={district}
                  onChange={(e) => setDistrict(e.target.value)}
                  label="District"
                >
                  {uniqueDistricts.map((dist, index) => (
                    <MenuItem key={index} value={dist}>
                      {dist}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
            <TextField
              margin="dense"
              label="Email"
              type="email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <TextField
              margin="dense"
              label="Phone"
              type="text"
              fullWidth
              value={phone}
              onChange={handlePhoneChange}
            />

            <TextField
              margin="dense"
              label="Password"
              type="password"
              fullWidth
              value={password}
              onChange={(e) => setPassword(e.target.value)}
            />
          </>
          {error && (
            <Typography color="error" variant="body2">
              {error}
            </Typography>
          )}
        </DialogContent>
        <ModalActions
          onSave={handleAddUser}
          saving={loading}
          saveLabel="Add User"
        />
      </Dialog>
    </>
  );
};

export default AddForm;
